<template>
  <v-stepper alt-labels v-model="currentStep" class="elevation-0">
    <p class="mb-0 mt-2 text-center">
      Adding categories to
      <span class="text-h6">{{ nodeName }}</span>
      <v-btn icon class="close-button elevation-0" @click="cancelDialog">
        <v-icon>mdi-close-circle</v-icon>
      </v-btn>
    </p>
    <v-stepper-header class="elevation-0">
      <v-stepper-step
        :step="this.steps.SELECT_CATEGORIES"
        :complete="isComplete(this.steps.SELECT_CATEGORIES)"
      >
        Select Categories
      </v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step
        :step="this.steps.ADD_TYPES"
        :complete="isComplete(this.steps.ADD_TYPES)"
      >
        Add Types
        <small>(For new only)</small>
      </v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step
        :step="this.steps.ADD_CONDITIONALS"
        :complete="isComplete(this.steps.ADD_CONDITIONALS)"
      >
        Add Conditionals
        <small>(For categories only)</small>
      </v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step
        :step="this.steps.CONFIRM"
        :complete="isComplete(this.steps.CONFIRM)"
      >
        Confirm
      </v-stepper-step>
    </v-stepper-header>
    <v-stepper-items class="stepper-scrollable-content">
      <v-stepper-content step="1">
        <v-container>
          <v-form ref="selectForm">
            <v-radio-group v-model="catType" row mandatory>
              <v-radio value="category">
                <template v-slot:label>
                  From a list of categories
                </template>
                >
              </v-radio>
              <v-radio value="forms">
                <template v-slot:label>
                  From saved forms
                </template>
              </v-radio>
            </v-radio-group>
            <v-combobox
              v-if="catType === 'category'"
              autofocus
              clearable
              :items="searchCategories"
              item-text="name"
              item-value="_id"
              chips
              deletable-chips
              clear-icon="mdi-close-circle"
              multiple
              label="Category names"
              v-model="selectedCategories"
              hide-selected
              :search-input.sync="search"
              @change="search = ''"
            >
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>
                      <p>
                        No Category matching "
                        <strong>{{ search }}</strong>
                        ". Press
                        <kbd>enter</kbd>
                        to create a new one
                      </p>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template v-slot:selection="{ attr, on, item, index, selected }">
                <v-chip
                  v-bind="attr"
                  :input-value="selected"
                  :color="getItemColor(item)"
                  class="white--text"
                  v-on="on"
                  close
                  @click:close="selectedCategories.splice(index, 1)"
                >
                  <span v-text="item.name"></span>
                </v-chip>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-avatar
                  color="#78909c"
                  class="text-h5 font-weight-light white--text"
                >
                  {{ item.name.charAt(0) }}
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title v-text="item.name"></v-list-item-title>
                  <v-list-item-subtitle
                    v-text="item.symbol"
                  ></v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-combobox>
            <v-autocomplete
              v-if="catType === 'forms'"
              autofocus
              :items="searchForms"
              item-value="_id"
              return-object
              item-text="name"
              label="Form names"
              auto-select-first
              v-model="selectedForms"
              clearable
              clear-icon="mdi-close-circle"
              multiple
              :rules="[
                () =>
                  !!selectedForms ||
                  catType === 'forms' ||
                  'Form name is required',
              ]"
            >
              <template #selection="{item, index}">
                <v-chip
                  color="transparent"
                  close
                  @click:close="selectedForms.splice(index, 1)"
                >
                  <v-icon :color="getItemColor(item)" class="mr-2">
                    mdi-file-tree
                  </v-icon>
                  {{ item.name }}
                </v-chip>
              </template>
            </v-autocomplete>
          </v-form>
        </v-container>
        <v-row align="center" justify="space-between" no-gutters>
          <v-btn outlined @click="cancelDialog">Cancel</v-btn>
          <v-btn
            :disabled="isDisabled"
            color="primary"
            @click.stop="
              setStep(catType === 'forms' ? steps.CONFIRM : steps.ADD_TYPES)
            "
          >
            Next
          </v-btn>
        </v-row>
      </v-stepper-content>
      <v-stepper-content step="2">
        <v-container>
          <v-form v-if="newCategories" ref="typesForm">
            <div v-for="(newCat, index) in newCategories" :key="index">
              <v-row class="align-baseline">
                <p class="text-h6 pr-2">
                  <strong>{{ newCat.name }}</strong>
                  <span class="body-1">&nbsp;is of type</span>
                </p>
                <v-select
                  v-model="newCat.meta.type"
                  :items="allowedTypes"
                  item-value="type"
                  item-text="name"
                  chips
                  class="white--text"
                ></v-select>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <TypeCreator
                    :node="newCat"
                    :selectedRecord="selectedRecord"
                    @validation="newCat.isValid = $event"
                  />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-container>
        <v-row align="center" justify="space-between" no-gutters>
          <v-btn outlined @click.stop="setStep(steps.SELECT_CATEGORIES)">
            Back
          </v-btn>
          <v-btn
            color="primary"
            @click.stop="setStep(steps.ADD_CONDITIONALS)"
            :disabled="!checkIfTypesAreValid()"
          >
            Next
          </v-btn>
        </v-row>
      </v-stepper-content>
      <v-stepper-content step="3">
        <v-container>
          <v-form v-if="selectedCategories" ref="condForm">
            <div v-for="(condCat, index) in selectedCategories" :key="index">
              <v-row class="align-baseline">
                <v-col>
                  <p class="text-h6 mb-0">
                    <strong>{{ condCat.name }}</strong>
                    <span class="body-1">
                      &nbsp;is of type
                      <span class="font-italic">{{ condCat.meta.type }}</span>
                    </span>
                  </p>
                  <conditionalCreator
                    class="border-bottom"
                    :node="condCat"
                    :parentNodeId="nodeId"
                    :selectedRecord="selectedRecord"
                  />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-container>
        <v-row align="center" justify="space-between" no-gutters>
          <v-btn outlined @click.stop="setStep(steps.ADD_TYPES)">
            Back
          </v-btn>
          <v-btn
            color="primary"
            @click.stop="setStep(steps.CONFIRM)"
            :disabled="!checkIfTypesAreValid()"
          >
            Next
          </v-btn>
        </v-row>
      </v-stepper-content>

      <v-stepper-content step="4">
        <span v-if="catType === 'category'">
          <p>
            Adding the following categories:
          </p>
          <v-form ref="addForm" class="ml-2">
            <div v-for="(cat, index) in selectedCategories" :key="index">
              <v-checkbox
                v-model="cat.meta.addConfirmed"
                class="font-weight-bold mt-0"
              >
                <template v-slot:label>
                  <span class="mr-2">
                    {{ cat.name }}
                    <span class="body-2">
                      [
                      <span class="font-weight-medium">
                        {{ useFriendlyTypeName(cat.meta.type) }}
                      </span>
                      <span v-if="cat.meta.uom">
                        ({{
                          cat.meta.uom.uom ? cat.meta.uom.uom : cat.meta.uom
                        }})
                      </span>
                      <span v-if="cat.meta.periodic_group">
                        , view {{ cat.meta.periodic_group }}
                      </span>
                      ]
                    </span>
                  </span>
                </template>
              </v-checkbox>
            </div>
            <v-divider></v-divider>
            <v-row align="center" no-gutters>
              <v-checkbox v-model="formName.confirmed" class="mt-0">
                <template v-slot:label>
                  <span class="mr-2">
                    Save
                    <span class="text-h6">{{ selectedRecord.name }}</span>
                    as a Form named
                  </span>
                </template>
              </v-checkbox>
              <v-text-field
                :disabled="!formName.confirmed"
                :required="formName.confirmed"
                v-model="formName.name"
              ></v-text-field>
            </v-row>
          </v-form>
        </span>
        <span v-else>
          <p>
            Adding the following forms:
          </p>
          <v-form ref="addForm" class="ml-2">
            <div v-for="(form, index) in selectedForms" :key="index">
              <v-checkbox
                v-model="form.meta.addConfirmed"
                class="font-weight-bold mt-0"
              >
                <template v-slot:label>
                  <span class="mr-2">
                    {{ form.name }}
                  </span>
                </template>
              </v-checkbox>
            </div>
          </v-form>
        </span>
        <v-row align="center" justify="space-between" no-gutters>
          <div>
            <v-btn
              class="mr-3"
              outlined
              @click.stop="
                setStep(
                  catType === 'forms'
                    ? steps.SELECT_CATEGORIES
                    : steps.ADD_CONDITIONALS
                )
              "
            >
              Back
            </v-btn>
            <v-btn outlined @click.stop="setStep(steps.SELECT_CATEGORIES)">
              Add More
            </v-btn>
          </div>
          <v-btn color="primary" @click="submitChanges" :disabled="!canSubmit">
            <v-progress-circular
              v-if="isSubmitting"
              class="mr-3"
              indeterminate
              :size="20"
              :width="1"
              color="secondary"
            ></v-progress-circular>
            Submit
          </v-btn>
        </v-row>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>
<script>
import { mapGetters, mapActions, mapState } from "vuex";
import TypeCreator from "../../components/TypeCreator.vue";
import conditionalCreator from "../record-navigator/add-categories/conditionalCreator.vue";
import isArray from "lodash/isArray";
import cloneDeep from "lodash/cloneDeep";
import { mergeTemplateToRecord } from "../../../utils/api";

export default {
  props: {
    categories: Array,
    nodeName: String,
    nodeId: String,
    selectedRecord: Object,
  },
  components: { TypeCreator, conditionalCreator },
  name: "AddCategories",
  created() {
    this.steps = {
      SELECT_CATEGORIES: 1,
      ADD_TYPES: 2,
      ADD_CONDITIONALS: 3,
      CONFIRM: 4,
    };
    this.allowedTypes = [
      { name: "Title", type: "title" },
      { name: "Text", type: "text" },
      { name: "Yes/No", type: "boolean" },
      { name: "Numeric", type: "numeric" },
      { name: "Numeric Scale", type: "numeric-scale" },
      { name: "Numeric Array", type: "numeric-array" },
      { name: "Text Array", type: "text-array" },
      { name: "Single Choice List", type: "value-list" },
      { name: "Multiple Value List", type: "multiple-value-list" },
      // { name: 'Time', type: 'time' },
      { name: "Date", type: "np-date" },
      { name: "Non Periodic Text", type: "np-text" },
      { name: "Phone Number", type: "np-phonenumber" },
    ];
  },

  data() {
    return {
      searchCategories: [
        { header: "Select a category or create one" },
        ...this.categories,
      ],
      currentStep: 1,
      selectedCategories: [],
      formName: { name: "", confirmed: false },
      search: "",
      isSubmitting: false,
      categoriesAdded: [],
      catType: "category",
      selectedForms: [],
      savenewlyeditedrecord: {},
    };
  },
  computed: {
    ...mapGetters("allTypes", { allTypes: "list" }, { root: true }),
    ...mapGetters("allUoms", { units: "list" }, { root: true }),
    ...mapGetters("allTemplates", { templates: "list" }, { root: true }),
    newCategories() {
      return this.selectedCategories.filter((thisCat) => thisCat.meta.new);
    },
    searchForms() {
      return this.templates
        ? [{ header: "Select forms" }, ...this.templates]
        : [];
    },
    canSubmit() {
      if (this.catType === "category") {
        const formIsValid =
          !this.formName.confirmed ||
          (this.formName.confirmed && this.formName.name);
        const atLeastOneChecked = this.selectedCategories.some(
          (cat) => cat.meta.addConfirmed
        );
        return formIsValid && atLeastOneChecked;
      } else {
        return this.selectedForms.some((form) => form.meta.addConfirmed);
      }
    },
    isDisabled() {
      return !(this.catType === "category"
        ? this.selectedCategories.length
        : this.selectedForms.length);
    },
  },
  methods: {
    setStep(nextStep) {
      if (nextStep === this.steps.ADD_TYPES) {
        // Check if the "meta.new" value is set on any object. If so, we have new objects that need types
        const needsTypes = this.selectedCategories.some(
          (thisCat) => thisCat.meta.new
        );

        // If we need types, go to the Types step
        // If not, go to add conditionals if we're going "forward"
        // else go to select categories if we're going "back"
        this.currentStep = needsTypes
          ? this.steps.ADD_TYPES
          : this.currentStep === this.steps.SELECT_CATEGORIES
          ? this.steps.ADD_CONDITIONALS
          : this.steps.SELECT_CATEGORIES;
      } else {
        this.currentStep = nextStep;
      }
    },
    isComplete(myStep) {
      return this.currentStep > myStep;
    },
    cancelDialog() {
      // Put up an "Are you sure?" dialog of some sort
      if (
        (this.selectedCategories.length || this.selectedForms.length) &&
        !window.confirm(
          `You've added ${
            this.catType === "categories" ? "categories" : "forms"
          }; are you sure you want to cancel?`
        )
      ) {
        return;
      }

      this.$refs.selectForm.reset();
      this.$refs.typesForm.reset();
      this.$refs.addForm.reset();
      this.currentStep = 1;
      this.$emit("cancel-add-dialog");
    },
    getItemColor(item) {
      return item.meta.new ? "#039BE5" : "#78909C";
    },
    async submitChanges() {
      this.isSubmitting = true;
      if (this.catType === "category") {
        await this.submitCategoryChanges();
      } else {
        await this.submitFormChanges();
      }
      this.isSubmitting = false;
      this.$emit("close-add-dialog");
    },
    async submitCategoryChanges() {
      // We have to wait for all the new Units of Measure to get added before we procceed
      // So wait on all the promises to be resolved.
      await Promise.all(this.addNewUoms());
      // We have to wait for all the categories to get added before we procceed
      // So wait on all the promises to be resolved.
      await Promise.all(this.addNewCategories());
      // Now add the desired selected (and new) categories to the parent
      const catsToAdd = this.selectedCategories.filter(
        (cat) => cat.meta.addConfirmed
      );
      // Use a for loop rather than map or forEach so the await will serialize the requests
      for (const cat of catsToAdd) {
        const params = {
          cat_id: cat._id,
          record_node_id: this.nodeId,
        };
        const record = await this.$store.dispatch(
          "addCatToRecord/mergeCat",
          params
        );
        if (record) {
          if (cat.meta["condition-node"]) {
            // Have to clone the category or it will get overwritten when updated
            const payload = {
              obj: cloneDeep(cat),
              root_id: this.selectedRecord._id,
            };
            await this.$store.dispatch(
              "updateRecordFromDB",
              this.selectedRecord._id
            );
            payload.obj._id = await this.findIdByParent(
              this.nodeId,
              this.selectedRecord.children
            );
            payload.obj.parent = this.nodeId;
            await this.$store.dispatch(
              "updateRecNode/updateRecordNode",
              payload
            );
          }
          this.categoriesAdded.push(cat.name);
        }
      }

      if (this.categoriesAdded.length !== 0) {
        const addedCatNames = this.categoriesAdded.join(", ");
        this.$store.dispatch("addNotification", {
          type: "success",
          message: `Added ${addedCatNames} to ${this.nodeName}`,
        });
        await this.$store.dispatch(
          "updateRecordFromDB",
          this.selectedRecord._id
        );
      } else {
        this.$store.dispatch("addNotification", {
          type: "warning",
          message: `No categories added to ${this.nodeName}`,
        });
      }
      if (this.formName.confirmed) {
        const params = {
          rec_id: this.selectedRecord._id,
          template_name: this.formName.name,
        };
        const response = await this.$store.dispatch(
          "createTemplate/createTemplate",
          params
        );
        if (response && response.Success) {
          this.$store.dispatch("addNotification", {
            type: "success",
            message: `Created new form ${this.formName.name}`,
          });
          this.$store.dispatch("allTemplates/templates", { rootState: true });
        } else {
          this.$store.dispatch("addNotification", {
            type: "errorMessage",
            message: `Could not create new form ${this.formName.name}`,
          });
        }
      }
    },
    async submitFormChanges() {
      const formsToAdd = this.selectedForms.filter(
        (form) => form.meta.addConfirmed
      );
      // Use a for loop rather than map or forEach so the await will serialize the requests
      for (const form of formsToAdd) {
        const params = {
          cat_id: this.nodeId,
          template_id: form._id,
        };
        const response = await mergeTemplateToRecord(params);
        if (response) {
          this.categoriesAdded.push(form.name);
        }
        this.isSubmitting = false;
        this.$emit("close-add-dialog");
        this.$store.dispatch("updateRecordFromDB", this.selectedRecord._id);
        if (this.categoriesAdded.length !== 0) {
          const addedCatNames = this.categoriesAdded.join(", ");
          this.$store.dispatch("addNotification", {
            type: "success",
            message: `Added ${addedCatNames} to ${this.nodeName}`,
          });
        } else {
          this.$store.dispatch("addNotification", {
            type: "warning",
            message: `No forms added to ${this.nodeName}`,
          });
        }
      }
    },
    addNewCategories() {
      const now = new Date().toISOString();

      return this.newCategories.map(async (cat) => {
        /* Create the new categories */
        const payload = {
          name: cat.name,
          child_count: 0,
          children: [],
          value: "",
          meta: {
            created: new Date().toISOString(),
            type: cat.meta.type,
            periodic: true,
            periodic_group: cat.meta.periodic_group,
            "condition-type": cat.meta["condition-type"],
            "condition-node": cat.meta["condition-node"],
            condition: cat.meta.condition,
            question: cat.meta.question,
            comment: cat.meta.comment,
            tooltip: cat.meta.tooltip,
          },
        };
        // Add type-specific attributes
        switch (cat.meta.type) {
          case "text":
            payload.value = "";
            break;
          case "boolean":
            payload.meta.choices = ["Yes", "No"];
            payload.value = "No";
            break;
          case "numeric":
            payload.meta.precision = cat.meta.precision;
            payload.meta.uom = cat.meta.uom;
            payload.meta.min = Number.isFinite(cat.meta.min)
              ? parseFloat(cat.meta.min, 10)
              : null;
            payload.meta.max = Number.isFinite(cat.meta.max)
              ? parseFloat(cat.meta.max, 10)
              : null;
            payload.value = cat.meta.min ? parseFloat(cat.meta.min, 10) : 0;
            break;
          case "numeric-scale":
            payload.meta.precision = cat.meta.precision;
            payload.meta.uom = cat.meta.uom;
            payload.meta.min = parseFloat(cat.meta.min, 10);
            payload.meta.max = parseFloat(cat.meta.max, 10);
            payload.meta.step = parseFloat(cat.meta.step, 10);
            payload.value = parseFloat(cat.meta.min, 10);
            break;
          case "numeric-array":
            payload.meta.precision = cat.meta.precision;
            payload.meta.uom = cat.meta.uom;
            payload.meta.min = Number.isFinite(cat.meta.min)
              ? parseFloat(cat.meta.min, 10)
              : null;
            payload.meta.max = Number.isFinite(cat.meta.max)
              ? parseFloat(cat.meta.max, 10)
              : null;
            payload.meta.separator = cat.meta.separator;
            payload.meta.value_name = cat.meta.value_name.concat();
            payload.value = cat.value;
            break;
          case "text-array":
            payload.meta.separator = cat.meta.separator;
            payload.meta.value_name = cat.meta.value_name.concat();
            payload.value = cat.value;
            break;
          case "value-list":
            payload.meta.choices = cat.meta.choices.concat();
            payload.value = null;
            break;
          case "multiple-value-list":
            payload.meta.choices = cat.meta.choices.concat();
            payload.value = cat.value;
            break;
          case "np-date":
            payload.meta.periodic = false;
            break;
          case "np-text":
            payload.meta.periodic = false;
            break;
          case "np-phonenumber":
            payload.meta.periodic = false;
            break;
        }
        // If this is a periodic entry, change the value to be an array of object(s)
        // Everything is periodic now so always do this.
        if (payload.meta.periodic) {
          const theValue = isArray(payload.value)
            ? payload.value
            : [payload.value];
          payload.value = [];
        }

        // If the UOM is an object, convert it to just the uom name
        if (payload.meta.uom) {
          payload.meta.uom = payload.meta.uom.uom
            ? payload.meta.uom.uom
            : payload.meta.uom;
        }

        // Create the new category
        const node = await this.$store.dispatch(
          "postNewCategory/createCat",
          payload
        );
        this.savenewlyeditedrecord = node;
        this.$store.dispatch("addNotification", {
          type: node ? "success" : "errorMessage",
          message: `${node ? "Created" : "Could not create"} new category ${
            payload.name
          } `,
        });
        if (node) {
          // Update the store with the new category
          this.$store.commit("allRootCats/updateCats", node);

          // Update the record in the selectedCategories array
          node.meta.addConfirmed = true;
          const index = this.selectedCategories.indexOf(cat);
          this.selectedCategories.splice(index, 1, node);
        }
      });
    },
    addNewUoms() {
      return this.newCategories
        .filter((cat) => cat.meta.uom && cat.meta.uom.new)
        .map(async (cat) => {
          // Create the new units of measure
          const payload = {
            name: cat.meta.uom.uom,
            ...cat.meta.uom,
          };
          delete payload.new;

          // Create the new uom
          const result = await this.$store.dispatch(
            "postNewUom/createUom",
            payload
          );
          this.$store.dispatch("addNotification", {
            type: result ? "success" : "errorMessage",
            message: `${
              result ? "Added" : "Could not add"
            } new unit of measure ${payload.uom} `,
          });
          if (result) {
            // Update the store with the new uom
            this.$store.commit("allUoms/updateUoms", result);
          }
        });
    },
    useFriendlyTypeName(type) {
      const theType = this.allowedTypes.find((item) => item.type === type);
      return theType.name;
    },
    checkIfTypesAreValid() {
      // Only check for valid types for new categories
      return this.selectedCategories
        .filter((newCat) => newCat.meta.new)
        .every((newCat) => newCat.isValid);
    },
    findIdByParent(parentid, children) {
      if (parentid === children[0].parent) {
        return children[children.length - 1]._id;
      }
      let id = null;

      for (let child of children) {
        if (parentid === child._id) {
          id = child.children[child.children.length - 1]._id;
        } else if (child.children.length > 0 && !id) {
          this.findIdByParent(parentid, child.children);
        }
      }
      return id;
    },
  },
  watch: {
    selectedCategories(val, prev) {
      if (val.length === prev.length) return;

      this.selectedCategories = val.map((v) => {
        if (typeof v === "string") {
          // Convert new category into an object
          v = {
            name: v,
            isValid: false,
            meta: {
              new: true,
              type: "title", // Assume a title field
              uom: "",
              min: null,
              max: null,
              step: 0,
              addConfirmed: true,
              value_name: [],
              periodic: false,
              periodic_group: "",
              "condition-type": false,
              "condition-node": "",
              condition: "",
              question: "",
              comment: "",
              tooltip: "",
            },
          };
        } else {
          // For exising objects, add the "addConfirmed" meta property if it doesn't already exist.
          if (v.meta.addConfirmed === undefined) {
            this.$set(v.meta, "addConfirmed", true);
          }
        }
        return v;
      });
    },
    selectedForms(val, prev) {
      if (val.length === prev.length) return;
      // Add the "addConfirmed" meta property if it doesn't already exist.
      this.selectedCategories = val.map((v) => {
        if (v.meta.addConfirmed === undefined) {
          this.$set(v.meta, "addConfirmed", true);
        }
        return v;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.close-button {
  position: absolute;
  right: 0px;
  cursor: pointer;
  z-index: 200;
  margin-top: -10px;
}
.v-stepper__content {
  padding-top: 0;
}
.full-width {
  width: 100%;
}
.border-bottom {
  padding-bottom: 5px;
  border-bottom: 1px dashed #ccc;
}
</style>
